import { storage } from '../../../js/utils/storage.js';
import { encodeMessageKey } from './message-key-utils.js';

const DEFAULT_VOICE_LANGUAGE = 'en';

const persistVoiceLanguages = (voicePreferences) => {
    try {
        storage.set('characterVoiceLanguages', voicePreferences);
    } catch (error) {
        console.warn('persistVoiceLanguage: Failed to save voice language:', error);
    }
};

export const persistVoiceLanguage = (characterId, language) => {
    if (!characterId || !language) return;
    const voicePreferences = storage.get('characterVoiceLanguages') || {};
    const nextLang = typeof language === 'string' ? language.trim() : '';
    if (!nextLang) return;

    if (nextLang === DEFAULT_VOICE_LANGUAGE) {
        if (!voicePreferences[characterId]) return;
        delete voicePreferences[characterId];
        persistVoiceLanguages(voicePreferences);
        return;
    }

    if (voicePreferences[characterId] === nextLang) return;
    voicePreferences[characterId] = nextLang;
    persistVoiceLanguages(voicePreferences);
};

export const getCurrentVoiceId = (characterId) => {
    const voicePreferences = storage.get('characterVoiceIds') || {};
    const preferredVoice = voicePreferences[characterId];
    if (typeof preferredVoice === 'string' && preferredVoice.trim() !== '') {
        return preferredVoice.trim();
    }
    return null;
};

export const getCurrentVoiceLanguage = (characterId) => {
    const voicePreferences = storage.get('characterVoiceLanguages') || {};
    if (voicePreferences[characterId]) {
        return voicePreferences[characterId];
    }

    const voiceDropdown = document.querySelector(`.character-voice-dropbtn[data-character-id="${characterId}"]`);
    if (voiceDropdown) {
        const flagImg = voiceDropdown.querySelector('.voice-flag-icon');
        if (flagImg && /** @type {HTMLImageElement} */ (flagImg).src) {
            const flagMatch = /** @type {HTMLImageElement} */ (flagImg).src.match(/\/([a-z]{2}(-[A-Z]{2})?)\.svg$/);
            if (flagMatch) {
                const flagCode = flagMatch[1];
                const flagToLanguage = { 'gb': 'en', 'jp': 'ja', 'kr': 'ko', 'fr': 'fr', 'es': 'es', 'cn': 'zh-CN' };
                const detectedLanguage = flagToLanguage[flagCode] || DEFAULT_VOICE_LANGUAGE;
                persistVoiceLanguage(characterId, detectedLanguage);
                return detectedLanguage;
            }
        }
    }
    return DEFAULT_VOICE_LANGUAGE;
};

export const getVoiceSettingsSnapshot = (characterId) => {
    const voiceLanguageRaw = getCurrentVoiceLanguage(characterId) || DEFAULT_VOICE_LANGUAGE;
    const voiceIdRaw = getCurrentVoiceId(characterId);

    const normalizedLanguage = typeof voiceLanguageRaw === 'string' && voiceLanguageRaw.trim() !== ''
        ? voiceLanguageRaw.trim()
        : DEFAULT_VOICE_LANGUAGE;

    const normalizedVoiceId = typeof voiceIdRaw === 'string' && voiceIdRaw.trim() !== ''
        ? voiceIdRaw.trim()
        : 'default';

    return {
        voiceLanguage: normalizedLanguage,
        voiceId: voiceIdRaw && voiceIdRaw.trim() !== '' ? voiceIdRaw.trim() : null,
        normalizedLanguage,
        normalizedVoiceId
    };
};

export const buildVoiceCompositeKey = (characterId, messageText, voiceSettings) => {
    const messageKey = encodeMessageKey(messageText);
    const languagePart = voiceSettings?.normalizedLanguage || DEFAULT_VOICE_LANGUAGE;
    const voicePart = voiceSettings?.normalizedVoiceId || 'default';
    return `${characterId}::${messageKey}::${languagePart}::${voicePart}`;
};

export const buildVoiceReadyKey = (characterId, messageText, voiceSettings) => {
    const messageKey = encodeMessageKey(messageText);
    const languagePart = voiceSettings?.normalizedLanguage || DEFAULT_VOICE_LANGUAGE;
    const voicePart = voiceSettings?.normalizedVoiceId || 'default';
    return `voice_ready_${characterId}_${messageKey}_${languagePart}_${voicePart}`;
};

export const getAudioCacheKey = (characterId, messageText, voiceSettings = null) => {
    const settings = voiceSettings || getVoiceSettingsSnapshot(characterId);
    const messageKey = encodeMessageKey(messageText);
    return `voice_audio_${characterId}_${messageKey}_${settings.normalizedLanguage}_${settings.normalizedVoiceId}`;
};
